import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EvidenceService } from '../api/evidence.service';
import { catchError, exhaustMap, map, of, switchMap, tap, filter, throwError } from 'rxjs';
import * as EvidenceDetailsActions from './data-access.actions';
import { AgencyBase, CadReportCategory, Device, EvidenceAndPreview, EvidenceDetails, Tag, Video, Location } from '../models/evidence.models';
import { EvidenceLink, EvidenceOfficerBase, EvidenceStatus } from '@shared/utils/evidence-management';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { NotificationService } from '@shared/feature/notification-handling';
import { EvidenceDetailsResponse } from '../models/evidence-response.models';
import { CadCategory, CategoryBase } from '@shared/utils/category';
import { HttpErrorResponse } from '@angular/common/http';
import { ActionType, EvidenceDetailsActionResult } from '../models/evidence-action-result.models';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { ModalService } from '@shared/feature/modal';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { TranslationFromJsonService } from '@shared/feature/translate';
import { CacheEvidencesInitiator, CacheService } from '@shared/utils/cache';

@Injectable()
export class EvidenceDetailsEffects {
  fetchEvidenceDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EvidenceDetailsActions.getEvidence),
      switchMap((action) =>
        this.service.getEvidenceById(action.uid).pipe(
          switchMap((evidence) => {
            const evidenceDetails = this.createEvidenceDetailsObject(evidence);
            if (evidence.Status === EvidenceStatus.Deleted || evidence.Status === EvidenceStatus.QueuedForDeletion) {
              return [EvidenceDetailsActions.getEvidenceSuccess({ evidenceAndPreview: new EvidenceAndPreview(evidenceDetails, null) })];
            }

            return this.service
              .getPreview(action.uid)
              .pipe(map((preview) => EvidenceDetailsActions.getEvidenceSuccess({ evidenceAndPreview: new EvidenceAndPreview(evidenceDetails, preview) })));
          }),
          catchError((error: HttpErrorResponse) => {
            if (error.error['Message'] === 'The lock code you entered is incorrect') {
              return [EvidenceDetailsActions.setEvidenceUid({ uid: action.uid }), EvidenceDetailsActions.setLockCodeRequired({ required: true })];
            }

            return throwError(() => error);
          })
        )
      )
    )
  );

  fetchEvidenceLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EvidenceDetailsActions.getEvidenceSuccess),
      filter((payload) => payload.evidenceAndPreview.evidence.status === EvidenceStatus.Active),
      switchMap(() =>
        this.service.getEvidenceLocation().pipe(
          map((location) =>
            EvidenceDetailsActions.getEvidenceLocationSuccess({
              location: new Location(location.LocationAddress, location.LocationDescription, location.LocationLatitude, location.LocationLongitude),
            })
          )
        )
      )
    )
  );

  fetchEvidenceLinks$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EvidenceDetailsActions.getEvidenceSuccess),
      filter((payload) => payload.evidenceAndPreview.evidence.status === EvidenceStatus.Active),
      switchMap(() =>
        this.service
          .getEvidenceLinks()
          .pipe(map((response) => EvidenceDetailsActions.getEvidenceLinksSuccess({ evidenceLinks: response.map((x) => new EvidenceLink(x.Uid, x.Type, x.Source, x.FileName)) })))
      )
    )
  );

  fetchEvidenceLinksWithUid$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EvidenceDetailsActions.setEvidenceUid),
      switchMap(() =>
        this.service
          .getEvidenceLinks()
          .pipe(map((response) => EvidenceDetailsActions.getEvidenceLinksSuccess({ evidenceLinks: response.map((x) => new EvidenceLink(x.Uid, x.Type, x.Source, x.FileName)) })))
      )
    )
  );

  updateEvidenceDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EvidenceDetailsActions.updateEvidence),
      exhaustMap((action) =>
        this.service.updateEvidence(action.updatedEvidenceDetails).pipe(
          tap(() => {
            this.notificationService.addSuccess(this.translationFromJsonService.instant('file-updated-successfully'));
            this.modalService.closeAllModals();
            this.cacheService.evidencesDataModified(CacheEvidencesInitiator.EvidenceUpdated);
          }),
          map((updatedEvidence) => EvidenceDetailsActions.updateEvidenceSuccess({ evidenceDetails: this.createEvidenceDetailsObject(updatedEvidence) })),
          catchError((error: HttpErrorResponse) =>
            of(
              EvidenceDetailsActions.updateEvidenceActionResult({
                actionResult: new EvidenceDetailsActionResult(ActionType.UpdateEvidenceDetails, error, false),
              })
            )
          )
        )
      )
    )
  );

  updateEvidenceLocation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EvidenceDetailsActions.updateEvidenceLocation),
      exhaustMap((payload) =>
        this.service.setEvidenceLocation(payload.location).pipe(
          tap(() => {
            this.notificationService.addSuccess(this.translationFromJsonService.instant('location-set-successfully'));
            this.modalService.closeAllModals();
          }),
          map((newLocation) =>
            EvidenceDetailsActions.updateEvidenceLocationSuccess({
              location: new Location(newLocation.LocationAddress, newLocation.LocationDescription, newLocation.LocationLatitude, newLocation.LocationLongitude),
            })
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              EvidenceDetailsActions.updateEvidenceActionResult({
                actionResult: new EvidenceDetailsActionResult(ActionType.UpdateEvidenceLocation, error, false),
              })
            )
          )
        )
      )
    )
  );

  setUpEvidenceDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EvidenceDetailsActions.setUpEvidenceDetails),
      switchMap((evidence) => {
        const evidenceDetails = this.createEvidenceDetailsObject(evidence.evidenceDetails);

        return [EvidenceDetailsActions.setUpEvidenceDetailsSuccess({ evidenceDetails: evidenceDetails })];
      })
    )
  );

  revertEvidence$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EvidenceDetailsActions.revertEvidence),
      exhaustMap((payload) =>
        this.service.revertEvidence(payload.isLocked).pipe(
          switchMap((response) =>
            this.service.getPreview(response.UID).pipe(
              map((preview) =>
                EvidenceDetailsActions.getEvidenceSuccess({
                  evidenceAndPreview: new EvidenceAndPreview(this.createEvidenceDetailsObject(response), preview),
                })
              )
            )
          )
        )
      )
    )
  );

  requestTranscription$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EvidenceDetailsActions.requestTranscription),
      exhaustMap((payload) =>
        this.service.requestTranscription(payload.uid).pipe(
          tap(() => this.notificationService.addSuccess(this.translationFromJsonService.instant('file-transcription-requested-successfully'))),
          map((transcriptionStatus) => EvidenceDetailsActions.updateTranscriptionStatus({ status: transcriptionStatus }))
        )
      )
    )
  );

  constructor(
    private actions$: Actions,
    private service: EvidenceService,
    private notificationService: NotificationService,
    private modalService: ModalService,
    private translationFromJsonService: TranslationFromJsonService,
    private cacheService: CacheService
  ) {}

  private createEvidenceDetailsObject(evidence: EvidenceDetailsResponse): EvidenceDetails {
    return new EvidenceDetails(
      evidence.StorageFileURL,
      evidence.ShareType,
      evidence.Categories.map(
        (x) =>
          new CategoryBase(
            x.Name,
            x.RetainDays,
            x.UseRetainDays,
            x.IsArchived,
            x.IsDefault,
            x.CadCategories === null ? [] : x.CadCategories.map((y) => new CadCategory(y.Name, y.Uid)),
            x.AppliedOnFiles,
            x.UID
          )
      ),
      evidence.Tags.map((x) => new Tag(x.Value, x.UID)),
      evidence.DescriptionCad,
      evidence.CadReportCategories.map((x) => new CadReportCategory(x.Name, x.Uid)),
      evidence.FileLocation,
      new Device(evidence.Device.Name, evidence.Device.SerialNumber, evidence.Device.Manufacturer, evidence.Device.Model, evidence.Device.DeviceType, evidence.Device.DeviceStatus),
      new AgencyBase(evidence.Customer.Name, evidence.Customer.ShortName, evidence.Customer.UID),
      evidence.RemoveSheduledOn,
      evidence.RemovedOn,
      evidence.Md5Checksum,
      evidence.Sha256Checksum,
      evidence.Title,
      evidence.OrganisationalID,
      evidence.OrganisationalIdCad,
      evidence.FileType,
      evidence.ThumbnailFullPath,
      evidence.CreatedOn,
      evidence.OriginalFileName,
      evidence.OriginalFileDate,
      evidence.Status,
      evidence.Description,
      new EvidenceOfficerBase(evidence.User.UID, evidence.User.FirstName, evidence.User.LastName, evidence.User.EmailAddress, evidence.User.PictureFullPath),
      evidence.IsLocked,
      evidence.CategoriesNames,
      evidence.ActualFileDate,
      evidence.VideoInfo ? new Video(evidence.VideoInfo.Duration) : null,
      evidence.UID,
      evidence.AiSummaryStatus,
      evidence.TranscriptionStatus,
      evidence.CanBeTranscribed,
      evidence.TotalCases
    );
  }
}
